import { ErrorMessageSection } from "./shared";

export function CouponsContent() {
  return (
    <>
      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">How Sugargoo Coupons Work — A Beginner-Friendly Explanation</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Sugargoo coupons function as digital discount vouchers that reduce your total order cost. They come in several forms: percentage-off coupons (e.g., 10% off your total), fixed-amount coupons (e.g., $5 off), shipping-specific coupons that reduce international delivery costs, and service fee waivers that eliminate certain processing charges.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Most coupons are tied to specific conditions — minimum order values, eligible product categories, first-time user restrictions, or expiration dates. Understanding these conditions is essential to maximizing your savings and avoiding the frustration of finding a great coupon that does not apply to your order.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          A well-maintained <strong>Sugargoo Spreadsheet</strong> often includes a coupon tracker section where community members share active codes, their conditions, and expiration dates — making it dramatically easier to find working discounts for your specific haul.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Types of Sugargoo Coupons Available in 2026</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { icon: "ri-gift-line", title: "Welcome Coupons", desc: "New users often receive discount vouchers upon registration. These typically offer the highest percentage discounts and are designed to encourage first-time orders." },
            { icon: "ri-calendar-event-line", title: "Seasonal Sales", desc: "Major events like 11.11, Black Friday, Chinese New Year, and mid-year sales bring the deepest discounts with sitewide coupon availability." },
            { icon: "ri-group-line", title: "Community Codes", desc: "Active Sugargoo communities on Reddit and Discord frequently share working coupon codes and limited-time promotional links exclusive to community members." },
          ].map((item) => (
            <div key={item.title} className="p-4 rounded-xl bg-background-100 border border-background-200/60 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                <i className={`${item.icon} text-primary-600`}></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-800 mb-2">{item.title}</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Pro Strategies for Maximum Coupon Savings</h2>
        <div className="p-5 rounded-xl bg-accent-50 border border-accent-200">
          <ul className="space-y-3">
            {[
              "Always check the coupon center before placing any order — unclaimed discounts are lost savings that could reduce your total by 5-15%.",
              "Combine shipping coupons with consolidated parcels for maximum cost reduction. A shipping coupon on an already-consolidated package delivers compounding savings.",
              "Follow Sugargoo on social media and enable notifications for flash sale announcements. Limited-time promo codes often appear with very short redemption windows.",
              "Use your Sugargoo Spreadsheet to track coupon validity periods and plan hauls around active discount windows. Strategic timing multiplies savings across multiple items.",
              "Compare percentage-off versus fixed-amount coupons against your specific order total. A 10% coupon saves more on a $200 order, while a $15 fixed coupon is better on a $100 order.",
            ].map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                <span className="text-accent-500 font-bold flex-shrink-0">{i + 1}.</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Common Coupon Mistakes to Avoid</h2>
        <div className="space-y-4">
          {[
            { title: "Not Checking Minimum Order Requirements", desc: "Many coupons require a minimum spend. Adding items you do not need just to reach the threshold defeats the purpose of saving money — only use coupons on orders you were already planning." },
            { title: "Ignoring Category Restrictions", desc: "Some coupons only apply to specific product categories or sellers. Always read the fine print before counting on a discount for items that may be excluded from the promotion." },
            { title: "Letting Coupons Expire", desc: "Coupons have expiration dates ranging from days to months. The 'I will use it later' approach often results in wasted savings. Plan hauls around active coupons rather than saving coupons for future hauls." },
            { title: "Assuming All Coupons Stack", desc: "Most Sugargoo coupons cannot be combined. Test coupon stacking during checkout — if only one applies, use the highest-value coupon and save others for future orders." },
          ].map((item) => (
            <div key={item.title} className="flex gap-4 p-4 rounded-xl bg-secondary-50 border border-secondary-200">
              <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-close-circle-line text-secondary-600"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">{item.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <ErrorMessageSection />
    </>
  );
}